import { ReactNode } from "react";
import { DayPicker } from "react-day-picker";
import { format } from "date-fns";
import {
  CounterInputV2,
  DropdownV2,
  FormCardV2,
  FormGridV2,
  MultiDropdownV2,
  OptionV2,
} from "../FormControlsV2";
import { ReactComponent as CalendarIcon } from "../../assets/svg/icons/calendar.svg";

export type PastureManagementFormFieldV2 =
  | "farm"
  | "area"
  | "microAreas"
  | "type"
  | "monitor"
  | "heightInput"
  | "heightOutput"
  | "occupationDays"
  | "restDays"
  | "rateIn"
  | "rateOut"
  | "range"
  | "executionPerDay"
  | "frequency"
  | "startDate";

export type PastureManagementFormValueV2 = {
  farm: OptionV2 | null;
  area: OptionV2 | null;
  microAreas: OptionV2[];
  type: OptionV2 | null;
  monitor: OptionV2 | null;
  heightInput: number;
  heightOutput: number;
  occupationDays: number;
  restDays: number;
  rateIn: number;
  rateOut: number;
  range: number;
  executionPerDay: number;
  frequency: OptionV2 | null;
};

export type PastureManagementFormErrorsV2 = Partial<
  Record<PastureManagementFormFieldV2, string>
>;

type PastureManagementFormV2Props = {
  value: PastureManagementFormValueV2;
  errors?: PastureManagementFormErrorsV2;
  farms: OptionV2[];
  areas: OptionV2[];
  microAreas: OptionV2[];
  managementTypes: OptionV2[];
  monitorTypes: OptionV2[];
  frequencyOptions: OptionV2[];
  selectedDays: boolean[];
  weekDays: string[];
  disabledDays: number[];
  startDate?: Date;
  endDate?: Date;
  disabled?: boolean;
  historyMode?: boolean;
  mapSlot?: ReactNode;
  onChange: (value: PastureManagementFormValueV2) => void;
  onFarmChange?: (option: OptionV2 | null) => void;
  onAreaChange?: (option: OptionV2 | null) => void;
  onMicroAreasChange?: (options: OptionV2[]) => void;
  onManagementTypeChange?: (option: OptionV2 | null) => void;
  onMonitorTypeChange?: (option: OptionV2 | null) => void;
  onFrequencyChange?: (option: OptionV2 | null) => void;
  onDayToggle?: (index: number) => void;
  onStartDateChange?: (date: Date) => void;
  onSubmit?: () => void;
};

function isType(value: PastureManagementFormValueV2, label: string) {
  return value.type?.label === label;
}

function isMonitor(value: PastureManagementFormValueV2, label: string) {
  return value.monitor?.label === label;
}

function needsSchedule(value: PastureManagementFormValueV2) {
  return !!value.type && value.type.label !== "Sem monitoramento";
}

function getScheduleLabel(value: PastureManagementFormValueV2) {
  if (!needsSchedule(value)) return "";
  if (!isType(value, "Em faixa") && value.frequency?.label === "Semanal") {
    return "Selecione os dias e a data da primeira tarefa";
  }
  if (!isType(value, "Em faixa") && value.frequency?.label === "Bisemanal") {
    return "Selecione o dia e a data da primeira tarefa";
  }
  if (
    isType(value, "Em faixa") ||
    value.frequency?.label === "Mensal" ||
    isMonitor(value, "Dias")
  ) {
    return "Selecione a data da primeira tarefa";
  }
  return "";
}

export function PastureManagementFormV2({
  value,
  errors = {},
  farms,
  areas,
  microAreas,
  managementTypes,
  monitorTypes,
  frequencyOptions,
  selectedDays,
  weekDays,
  disabledDays,
  startDate,
  endDate,
  disabled = false,
  historyMode = false,
  mapSlot,
  onChange,
  onFarmChange,
  onAreaChange,
  onMicroAreasChange,
  onManagementTypeChange,
  onMonitorTypeChange,
  onFrequencyChange,
  onDayToggle,
  onStartDateChange,
}: PastureManagementFormV2Props) {
  const isRange = isType(value, "Em faixa");
  const isNoMonitoring = isType(value, "Sem monitoramento");
  const isReadOnly = disabled || historyMode;
  const scheduleLabel = getScheduleLabel(value);
  const showWeekDays =
    !isRange &&
    (value.frequency?.label === "Semanal" ||
      value.frequency?.label === "Bisemanal");

  function update(partial: Partial<PastureManagementFormValueV2>) {
    onChange({ ...value, ...partial });
  }

  return (
    <form className="ov2-property-form ov2-batch-form">
      <FormCardV2>
        <div className="pasture-management-v2">
          <div className="pasture-management-v2__fields">
            {historyMode && (
              <div className="pasture-management-v2__history-dates">
                <div className="pasture-management-v2__date-tag">
                  <small>De</small>
                  <div>
                    <h6>
                      {startDate ? format(startDate, "dd/MM/yyyy") : null}
                    </h6>
                    <CalendarIcon />
                  </div>
                </div>
                <div className="pasture-management-v2__date-tag">
                  <small>Até</small>
                  <div>
                    <h6>{endDate ? format(endDate, "dd/MM/yyyy") : null}</h6>
                    <CalendarIcon />
                  </div>
                </div>
              </div>
            )}

            <FormGridV2>
              <DropdownV2
                name="farm"
                label="Fazenda"
                value={value.farm}
                options={farms}
                searchable
                disabled={isReadOnly}
                error={errors.farm}
                emptyMessage="Nenhuma fazenda disponível"
                onChange={(farm) => {
                  onFarmChange && onFarmChange(farm);
                  update({ farm, area: null, microAreas: [] });
                }}
              />
              <DropdownV2
                name="area"
                label="Retiro"
                value={value.area}
                options={areas}
                searchable
                disabled={isReadOnly || !value.farm}
                error={errors.area}
                emptyMessage="Nenhum retiro disponível"
                onChange={(area) => {
                  onAreaChange && onAreaChange(area);
                  update({ area, microAreas: [] });
                }}
              />
              <MultiDropdownV2
                name="microAreas"
                label="Pastos"
                value={value.microAreas}
                options={microAreas}
                searchable
                disabled={isReadOnly || !value.area}
                error={errors.microAreas}
                emptyMessage="Nenhum pasto disponível"
                onChange={(selected) => {
                  onMicroAreasChange && onMicroAreasChange(selected);
                  update({ microAreas: selected });
                }}
              />
              <DropdownV2
                name="type"
                label="Tipo de Pastejo"
                value={value.type}
                options={managementTypes}
                searchable
                disabled={isReadOnly}
                error={errors.type}
                emptyMessage="Nenhum tipo de pastejo disponível"
                onChange={(type) => {
                  const rangeMonitor =
                    type?.label === "Em faixa" &&
                    Array.isArray(type.info) &&
                    type.info[0]
                      ? { label: type.info[0].name, value: type.info[0].id }
                      : null;
                  onManagementTypeChange && onManagementTypeChange(type);
                  update({
                    type,
                    monitor: rangeMonitor,
                    frequency: null,
                    heightInput: 0,
                    heightOutput: 0,
                    occupationDays: 0,
                    restDays: 0,
                    rateIn: 0,
                    rateOut: 0,
                    range: 0,
                    executionPerDay: 0,
                  });
                }}
              />
              {!isRange && !isNoMonitoring && monitorTypes.length > 0 && (
                <DropdownV2
                  name="monitor"
                  label="Tipo de Monitoramento"
                  value={value.monitor}
                  options={monitorTypes}
                  searchable
                  disabled={isReadOnly}
                  error={errors.monitor}
                  emptyMessage="Nenhum tipo de monitoramento disponível"
                  onChange={(monitor) => {
                    onMonitorTypeChange && onMonitorTypeChange(monitor);
                    update({
                      monitor,
                      frequency:
                        monitor?.label === "Dias" ? null : value.frequency,
                    });
                  }}
                />
              )}

              {isMonitor(value, "Altura") && (
                <>
                  <CounterInputV2
                    name="heightInput"
                    label="Altura da entrada"
                    value={value.heightInput}
                    max={999}
                    suffix="cm"
                    disabled={isReadOnly}
                    error={errors.heightInput}
                    onChange={(heightInput) =>
                      update({
                        heightInput,
                        heightOutput:
                          value.heightOutput > heightInput
                            ? heightInput
                            : value.heightOutput,
                      })
                    }
                  />
                  <CounterInputV2
                    name="heightOutput"
                    label="Altura da saída"
                    value={value.heightOutput}
                    max={value.heightInput || 999}
                    suffix="cm"
                    disabled={isReadOnly}
                    error={errors.heightOutput}
                    onChange={(heightOutput) => update({ heightOutput })}
                  />
                </>
              )}

              {isMonitor(value, "Dias") && (
                <>
                  <CounterInputV2
                    name="occupationDays"
                    label="Dias de ocupação"
                    value={value.occupationDays}
                    max={999}
                    suffix="dias"
                    disabled={isReadOnly}
                    error={errors.occupationDays}
                    onChange={(occupationDays) => update({ occupationDays })}
                  />
                  <CounterInputV2
                    name="restDays"
                    label="Dias de descanso"
                    value={value.restDays}
                    max={999}
                    suffix="dias"
                    disabled={isReadOnly}
                    error={errors.restDays}
                    onChange={(restDays) => update({ restDays })}
                  />
                </>
              )}

              {isMonitor(value, "Nota") && (
                <>
                  <CounterInputV2
                    name="rateIn"
                    label="Nota de entrada do pasto (0 a 10)"
                    value={value.rateIn}
                    max={10}
                    disabled={isReadOnly}
                    error={errors.rateIn}
                    onChange={(rateIn) =>
                      update({
                        rateIn,
                        rateOut:
                          value.rateOut > rateIn ? rateIn : value.rateOut,
                      })
                    }
                  />
                  <CounterInputV2
                    name="rateOut"
                    label="Nota de saída do pasto (0 a 10)"
                    value={value.rateOut}
                    max={value.rateIn || 10}
                    disabled={isReadOnly}
                    error={errors.rateOut}
                    onChange={(rateOut) => update({ rateOut })}
                  />
                </>
              )}

              {isRange && (
                <>
                  <CounterInputV2
                    name="range"
                    label="Quantidade em faixas"
                    value={value.range}
                    max={999}
                    disabled={isReadOnly}
                    error={errors.range}
                    onChange={(range) => update({ range })}
                  />
                  <CounterInputV2
                    name="executionPerDay"
                    label="Quantidade de execuções por dia"
                    value={value.executionPerDay}
                    max={999}
                    disabled={isReadOnly}
                    error={errors.executionPerDay}
                    onChange={(executionPerDay) => update({ executionPerDay })}
                  />
                </>
              )}

              {(isMonitor(value, "Altura") || isMonitor(value, "Nota")) && (
                <DropdownV2
                  name="frequency"
                  label="Frequência de monitoramento"
                  value={value.frequency}
                  options={frequencyOptions}
                  disabled={isReadOnly}
                  error={errors.frequency}
                  emptyMessage="Nenhuma frequência disponível"
                  onChange={(frequency) => {
                    onFrequencyChange && onFrequencyChange(frequency);
                    update({ frequency });
                  }}
                />
              )}
            </FormGridV2>

            {!historyMode && scheduleLabel && (
              <span className="pasture-management-v2__schedule-label">
                {scheduleLabel}
              </span>
            )}

            {showWeekDays && (
              <div className="pasture-management-v2__week-days">
                {weekDays.map((day, index) => (
                  <button
                    className={`pasture-management-v2__week-day ${
                      selectedDays[index]
                        ? "pasture-management-v2__week-day--selected"
                        : ""
                    }`}
                    key={day}
                    type="button"
                    disabled={isReadOnly}
                    onClick={() => onDayToggle && onDayToggle(index)}
                  >
                    {day}
                  </button>
                ))}
              </div>
            )}

            {!historyMode && needsSchedule(value) && (
              <div className="pasture-management-v2__day-picker">
                <DayPicker
                  mode="single"
                  selected={startDate}
                  fromDate={new Date()}
                  disabled={{ dayOfWeek: disabledDays }}
                  onDayClick={(day) =>
                    onStartDateChange && onStartDateChange(day)
                  }
                  showOutsideDays
                  fixedWeeks
                />
                {errors.startDate && (
                  <small className="ov2-field__error animation__fadeIn">
                    {errors.startDate}
                  </small>
                )}
              </div>
            )}
          </div>

          {mapSlot && (
            <div className="pasture-management-v2__map">{mapSlot}</div>
          )}
        </div>
      </FormCardV2>
    </form>
  );
}
